import Toast from 'react-native-toast-message';
import {autorun, makeAutoObservable, runInAction} from 'mobx';
import {makeLoggable} from 'mobx-log';

import {ERROR_MESSAGE, SUCCESS_CHECK} from '../components/ToastBottom';
import {GiftCardData} from '../giftCardShop/screens/schema';
import {AirtablePromotionItem} from '../models/airtable';
import {AppRoute} from '../models/AppRoute';
import {
  Address,
  CreateZenGiftCardPaymentInstrumentRequest,
  CreateZenGiftCardPaymentInstrumentResponse,
  ZenGiftCardsPaymentInstrument,
} from '../models/mfl';
import {GiftCardRecipientData} from '../models/middleware';
import {type OrderProcessingScreenProps} from '../screens/OrderProcessingScreen';
import apiService from '../transport/apiService';
import {
  Day,
  DeliveryTimesResponse,
  OrderLine,
  OrderResponse,
  PickupSlot,
  PickupTimesResponse,
} from '../transport/apiServiceResponse';
import {Analytics} from '../utils/analytics/analytics';
import {EventName, EventScreenName} from '../utils/analytics/events';
import {sum} from '../utils/functions';
import {isSummerCashGrabCompetition} from '../utils/games/summerCashGrab';
import {ZenGiftCardsPaymentInstrumentWithChargedAmount} from '../utils/giftCards';
import {isUnhandledMFL500Error} from '../utils/mfl';
import {isString} from '../utils/string';

import type {RootStore} from './rootStore';

const UPDATE_DEBOUNCE_TIME = 1250;
let timeout: any = null;

// Delivery notes on who is accepting the delivery. Gets added to the order
// delivery instructions, but stripped back out from the notes for display.
const REGEX_DELIVERY_WORDING = /\*{2}(ACCEPTING )?DELIVER(Y)?( TO)?:.*\*{2}/;
const NOMINATED_DELIVERY = 'ACCEPTING DELIVERY: ';
const SELF_DELIVERY = 'DELIVER TO: ';
const CHILL_DRINKS = '**CHILL DRINKS**\n';

type OrderUpdate = {
  productId: string;
  quantity: number;
};

const isDeliveryOrderLineWithCharge = (ol: OrderLine) =>
  ol.charge && ol.name === 'Delivery Fee';

const isPickupOrderLineWithCharge = (ol: OrderLine) =>
  ol.charge && ol.name === 'Free Pickup';

const getOrderTotalAmount = (order: any, switchingDeliveryPickup: boolean) => {
  let orderTotalAmount = 0;
  if (!switchingDeliveryPickup) {
    orderTotalAmount = order?.balance_at_checkout || 0;
  } else {
    orderTotalAmount =
      order?.order_lines
        ?.filter(
          (ol: OrderLine) =>
            !(
              isDeliveryOrderLineWithCharge(ol) ||
              isPickupOrderLineWithCharge(ol)
            ),
        )
        .map((ol: OrderLine) => ol.subtotal?.cents_inc_tax)
        .reduce(sum, 0) || 0;
    if (order?.adjustments) {
      orderTotalAmount +=
        order.adjustments
          ?.filter(
            (adj: any) =>
              !!adj.name && !adj.name.toLowerCase().includes('delivery'),
          )
          .map((adj: any) => adj.amount.cents_inc_tax)
          .reduce(sum, 0) || 0;
    }
  }
  return orderTotalAmount;
};

const readjustGiftCardsChargeAmounts = (
  order: any,
  zenGiftCardsUserPaymentInstruments: ZenGiftCardsPaymentInstrumentWithChargedAmount[],
  isSwitchingDeliveryPickup: boolean,
) => {
  let remaining = getOrderTotalAmount(order, isSwitchingDeliveryPickup);

  const giftCardsWithReadjustedChargeAmounts =
    zenGiftCardsUserPaymentInstruments.map(card => {
      const chargedAmount = Math.min(card.balance, remaining);
      remaining = Math.max(0, remaining - chargedAmount);
      return {
        ...card,
        chargedAmount,
      };
    });
  return giftCardsWithReadjustedChargeAmounts;
};

const getZenGiftCardRecipientFormValuesFromOrder = (order: OrderResponse) => {
  if (!order?.gift_information) return null;

  const {
    recipient_name,
    recipient_email_address,
    recipient_phone_number,
    note,
    gift_email_date,
  } = order.gift_information;

  if (
    recipient_name &&
    recipient_email_address &&
    recipient_phone_number &&
    note &&
    gift_email_date
  ) {
    return {
      recipientName: recipient_name,
      recipientEmail: recipient_email_address,
      recipientPhoneNumber: recipient_phone_number,
      deliveryDate: new Date(gift_email_date),
      note,
    };
  }

  return null;
};

export class CartStore {
  /**
   * callback to run after updatedorder
   */
  private _afterUpdate: any;
  rootStore: RootStore | null = null;
  currentOrder: any = null;
  submittingOrder: boolean = false;
  recentOrders: any[] = [];
  isLoadingOrder: boolean = true;

  // This is a buffer of update to order lines that will
  // get synced off to MFL periodically.
  orderUpdate: OrderUpdate[] = []; // products/qty to update current order with
  dirtyOrder: boolean = false; // details have changed but not submitted yet
  updatingOrder: boolean = false; // update sent to MFL waiting response
  loadingRecentOrders: boolean = true; // recent orders for user are loading
  orderProcessing: boolean = false; // order has been submitted to MFL waiting for checkout state
  orderCheckedOutSuccess: boolean = false;
  orderFailedPayment: boolean = false;
  orderPaymentIncomplete: boolean = false;
  orderPayment3DSChallenged: boolean = false;

  // Eligible competitions
  eligibleCompetitions: any[] = [];

  // Timeslots
  days: Day[] = [];
  selectedDay: Day | null = null;
  selectedSlot: ReturnType<typeof this.transformMFLSlot> | null = null;
  selectedSlotDirty: boolean = false;
  pickupSlots: Record<Day, ReturnType<typeof this.transformMFLSlot>[]> = {};
  loadingDeliverySlots: boolean = true;
  /**
   * if noDeliverySlotsAvailable it indicates address is not in a serviceable area for that store
   */
  noDeliverySlotsAvailable: boolean = false;
  deliverySlots: Record<Day, ReturnType<typeof this.transformMFLSlot>[]> = {};
  loadingPickupSlots: boolean = true;
  switchingDeliveryPickup: boolean = false; // Do we hide delivery/pickup charge from display

  // Order details from user
  isDelivery: boolean = false;
  isPickup: boolean = true;
  deliveryAddress: Address | null = null;
  deliveryAddressDirty: boolean = false;
  deliveryRegion: any = null;
  deliveryInstructions: string = '';
  deliveryInstructionsDirty: boolean = false;
  _orderNotes: string = '';
  chillDrinks: boolean = false;

  // Nominated person for delivery
  willBePresent: boolean = false;
  hasNominatedPerson: boolean = false;
  nominatedPersonName: string = '';
  nominatedPersonDirty: boolean = false;

  // Force a loading of region from delivery address when first loading the order
  forceLoadRegion: boolean = true;

  // Products promoted at checkout
  promotedAtCheckoutProducts: any = null;

  // Running total of the order
  totalAmount: number = 0; // cents

  // Gift Card orders
  zenGiftCardRecipientFormValues: GiftCardData | null = null;
  zenGiftCardsUserPaymentInstruments: ZenGiftCardsPaymentInstrumentWithChargedAmount[] =
    [];

  /**
   * activated exclusive offers
   */
  activatedDeals: string[] = [];

  reset = () =>
    runInAction(() => {
      this.currentOrder = null;
      this.days = [];
      this.deliveryAddress = null;
      this.deliveryAddressDirty = false;
      this.deliveryInstructions = '';
      this.deliveryInstructionsDirty = false;
      this.deliveryRegion = null;
      this.deliverySlots = {};
      this.dirtyOrder = false;
      this.eligibleCompetitions = [];
      this.hasNominatedPerson = false;
      this.isDelivery = false;
      this.isLoadingOrder = true;
      this.isPickup = true;
      this.loadingDeliverySlots = true;
      this.loadingPickupSlots = true;
      this.loadingRecentOrders = true;
      this.nominatedPersonDirty = false;
      this.nominatedPersonName = '';
      this.orderCheckedOutSuccess = false;
      this.orderFailedPayment = false;
      this.orderNotes = '';
      this.orderPayment3DSChallenged = false;
      this.orderPaymentIncomplete = false;
      this.orderProcessing = false;
      this.orderUpdate = [];
      this.pickupSlots = {};
      this.promotedAtCheckoutProducts = null;
      this.recentOrders = [];
      this.selectedDay = null;
      this.selectedSlot = null;
      this.selectedSlotDirty = false;
      this.submittingOrder = false;
      this.switchingDeliveryPickup = false;
      this.totalAmount = 0;
      this.updatingOrder = false;
      this.willBePresent = false;
      this.zenGiftCardRecipientFormValues = null;
      this.zenGiftCardsUserPaymentInstruments = [];
      this.chillDrinks = false;
      this.activatedDeals = [];
      this._afterUpdate = null;
    });

  /**
   * This set from api payload.
   * Its intended that this._orderNotes not contain the chill drinks message
   */
  set orderNotes(value) {
    runInAction(() => {
      this.chillDrinks = (value || '').indexOf(CHILL_DRINKS) > -1;
      this._orderNotes = (value || '').replaceAll(CHILL_DRINKS, '');
    });
  }

  /**
   * Returns orderNotes without chill drinks. This is intended when displaying to customer so shouldn't contain chill drinks message.
   */
  get orderNotes() {
    return (this._orderNotes || '').replaceAll(CHILL_DRINKS, '');
  }

  debounceUpdate = () => {
    const later = () => {
      timeout = null;
      this.updateOrder();
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, UPDATE_DEBOUNCE_TIME);
  };

  constructor(rootStore: any) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
    makeLoggable(this);

    // Update order on changes to lines
    autorun(() => {
      if (this.orderUpdate.length) {
        this.debounceUpdate();
      }
    });

    // Update particular details in our store once an order is loaded
    autorun(() => {
      if (this.currentOrder) {
        // Don't show delivery/pickup charge line if it exists on the order and doesn't match the
        // selected option in the store
        const hasDeliveryCharge = !!this.currentOrder?.order_lines?.find(
          isDeliveryOrderLineWithCharge,
        );
        const hasPickupCharge = !!this.currentOrder?.order_lines?.find(
          isPickupOrderLineWithCharge,
        );

        // We don't want to be updating again while we're reading back the values
        // of the order we just got back from an API call.
        runInAction(() => {
          this.selectedSlotDirty = false;
          this.deliveryAddressDirty = false;
          this.nominatedPersonDirty = false;
          this.deliveryInstructionsDirty = false;
        });

        runInAction(() => {
          this.orderNotes = this.currentOrder?.note;
          if (this.currentOrder.delivery_address) {
            this.deliveryAddress = this.currentOrder.delivery_address;
            if (this.currentOrder.delivery_address?.instructions) {
              // Handle delivery person notes. Strip out the person's name
              // and type of delivery, set the appropriate delivery type
              // and nominated person's name if applicable.
              let name;
              let type;
              const parts =
                this.currentOrder.delivery_address.instructions.split('\n');
              if (parts[parts.length - 1].match(REGEX_DELIVERY_WORDING)) {
                name = parts.pop();
                name = name.replaceAll('*', '');
                // nominated delivery person, don't care about self deliveries.
                if (name.startsWith(NOMINATED_DELIVERY)) {
                  name = name.replaceAll(NOMINATED_DELIVERY, '');
                  type = NOMINATED_DELIVERY;
                }
              }
              if (type === NOMINATED_DELIVERY) {
                this.nominatedPersonName = name;
                this.hasNominatedPerson = true;
                this.willBePresent = false;
              } else {
                this.willBePresent = true;
                this.hasNominatedPerson = false;
              }
              const instructions = parts.filter((p: any) => !!p).join('\\n');
              if (instructions.length) {
                this.deliveryInstructions = instructions;
              }
            }
          }
          if (this.currentOrder.time_slot) {
            let [from, to] = [...this.currentOrder.time_slot.checkout_window];
            const fromDate = Date.parse(from);
            const toDate = Date.parse(to);
            const now = new Date().getTime();

            if (fromDate < now && now < toDate) {
              this.selectedDay = this.currentOrder.time_slot.date;
              this.selectedSlot = this.transformMFLSlot(
                this.currentOrder.time_slot,
              );
            }
            if (
              this.currentOrder.time_slot.delivery_zone.type.toLowerCase() ===
              'pickup'
            ) {
              this.isPickup = true;
              this.isDelivery = false;
              this.switchingDeliveryPickup = hasDeliveryCharge;
            } else {
              this.isPickup = false;
              this.isDelivery = true;
              this.switchingDeliveryPickup = hasPickupCharge;
            }
          }
          if (this.currentOrder.activated_deals) {
            this.activatedDeals = this.currentOrder.activated_deals;
          }
        });

        if (this.forceLoadRegion && this.currentOrder.delivery_address) {
          runInAction(() => (this.forceLoadRegion = false));
          this.loadRegionForPostCode();
        }
      }
    });

    // Keep a running total for display, keeping in mind that the user may be switching between
    // pickup and delivery
    autorun(() => {
      const orderTotalAmount = getOrderTotalAmount(
        this.currentOrder,
        this.switchingDeliveryPickup,
      );
      runInAction(() => {
        this.totalAmount = orderTotalAmount;
      });
    });

    // Update delivery details on order when we have enough information to save to MFL.
    autorun(() => {
      // The info we care about for delivery details (instructions are optional, so don't bother checking them here)
      if (
        this.isDelivery &&
        this.deliveryAddress &&
        this.selectedSlot &&
        (this.willBePresent ||
          (this.hasNominatedPerson && this.nominatedPersonName))
      ) {
        //  Are they actually dirty and need to be updated
        if (
          this.deliveryAddressDirty ||
          this.selectedSlotDirty ||
          this.nominatedPersonDirty ||
          this.deliveryInstructionsDirty
        ) {
          this.updateDeliveryDetails();
        }
      }
    });

    // Update detail on pick up changes
    autorun(() => {
      if (this.isPickup && this.selectedSlot && this.selectedSlotDirty) {
        this.updateDeliveryDetails();
      }
    });

    // Load the delivery region whenever the delivery address is changed
    autorun(() => {
      if (this.deliveryAddress && this.deliveryAddressDirty) {
        this.loadRegionForPostCode();
      }
    });
  }

  setDelivery = (clear: boolean) => {
    const switching = !!this.currentOrder?.order_lines?.find(
      isPickupOrderLineWithCharge,
    );
    runInAction(() => {
      this.isDelivery = true;
      this.isPickup = false;
      this.switchingDeliveryPickup = switching;
      if (clear) {
        this.selectedSlot = null;
      }
    });
  };

  setPickup = (clear: boolean) => {
    const isSwitchingDeliveryPickup = !!this.currentOrder?.order_lines?.find(
      isDeliveryOrderLineWithCharge,
    );
    runInAction(() => {
      this.isDelivery = false;
      this.isPickup = true;
      this.switchingDeliveryPickup = isSwitchingDeliveryPickup;
      if (clear) {
        this.selectedSlot = null;
      }
    });
  };

  // Transform variable names AND strip timezones for display. We're not interested in
  // getting the local date/time.
  transformMFLSlot = (slot: PickupSlot) => {
    return {
      dropOffWindow: slot.drop_off_window.map(
        (time: string) => time.split('+')[0],
      ),
      checkoutWindow: slot.checkout_window.map(
        (time: string) => time.split('+')[0],
      ),
      id: slot.id,
      date: slot.date,
    };
  };

  loadPickupSlots = () => {
    runInAction(() => (this.loadingPickupSlots = true));
    apiService
      .getPickupTimeSlots()
      .then((data: unknown) => {
        const slots: Record<Day, ReturnType<typeof this.transformMFLSlot>[]> =
          {};
        (data as PickupTimesResponse).forEach(slot => {
          if (!slots[slot.date]) {
            slots[slot.date] = [];
          }
          slots[slot.date].push(this.transformMFLSlot(slot));
        });
        runInAction(() => {
          this.days = Object.keys(slots) as Day[];
          this.pickupSlots = slots;
          this.loadingPickupSlots = false;
        });
      })
      .catch(err => {
        runInAction(() => {
          this.days = [];
          this.selectedDay = null;
          this.pickupSlots = {};
          this.loadingPickupSlots = false;
        });
        console.error('unable to load pickup slots', err);
      });
  };

  loadDeliverySlots = () => {
    runInAction(() => {
      this.noDeliverySlotsAvailable = false;
      this.loadingDeliverySlots = true;
    });
    apiService
      .getDeliveryTimeSlots(this.deliveryRegion.id)
      .then((data: unknown) => {
        console.log('delivery slots', data);
        if (!(data as any[]).length) {
          runInAction(() => {
            this.days = [];
            this.selectedDay = null;
            this.deliverySlots = {};
            this.loadingDeliverySlots = false;
            this.noDeliverySlotsAvailable = true;
          });
        } else {
          const slots: Record<Day, ReturnType<typeof this.transformMFLSlot>[]> =
            {};
          (data as DeliveryTimesResponse).forEach(slot => {
            if (!slots[slot.date]) {
              slots[slot.date] = [];
            }
            slots[slot.date].push(this.transformMFLSlot(slot));
          });
          runInAction(() => {
            this.days = Object.keys(slots) as Day[];
            this.deliverySlots = slots;
            this.loadingDeliverySlots = false;
          });
        }
      })
      .catch(err => {
        runInAction(() => {
          this.days = [];
          this.selectedDay = null;
          this.deliverySlots = {};
          this.loadingDeliverySlots = false;
        });
        console.error('unable to load delivery slots', err);
      });
  };

  selectDay = (day: Day | null) => (this.selectedDay = day);

  selectSlot = (
    slot: ReturnType<typeof this.transformMFLSlot> | null,
    callback?: any,
  ) =>
    runInAction(() => {
      this.selectedSlot = slot;
      this.selectedSlotDirty = true;
      if (callback) {
        this._afterUpdate = callback;
      }
    });

  setDeliveryAddress = (address: Address) =>
    runInAction(() => {
      this.noDeliverySlotsAvailable = false;
      this.deliveryAddress = address;
      this.deliveryAddressDirty = true;
    });

  loadRegionForPostCode = () =>
    apiService
      .getRegions(this.deliveryAddress?.postal_code)
      .then((data: any) => {
        console.log('region', data);
        const region = data.find(
          (d: {name: any}) =>
            d.name === this.deliveryAddress?.locality.toUpperCase(),
        );
        if (region) {
          runInAction(() => {
            this.deliveryRegion = region;
          });
        } else {
          console.log('unable to match delivery region to delivery suburb');
        }
      })
      .catch(() => console.error('unable to get region for delivery address'));

  setDeliveryInstructions = (instructions: string) =>
    runInAction(() => {
      this.deliveryInstructions = instructions;
      this.deliveryInstructionsDirty = true;
    });

  /**
   * This is set from UI. won't include chill drinks in notes
   * @param notes
   * @returns
   */
  setOrderNotes = (notes: string) =>
    runInAction(() => (this._orderNotes = notes));

  setWillBePresent = (value: boolean) =>
    runInAction(() => {
      this.willBePresent = value;
      this.nominatedPersonDirty = true;
    });

  // Only set dirty if the name is present.
  setHasNominatedPerson = (value: boolean) => {
    if (this.nominatedPersonName) {
      runInAction(() => {
        this.hasNominatedPerson = value;
        this.nominatedPersonDirty = true;
      });
    } else {
      runInAction(() => (this.hasNominatedPerson = value));
    }
  };

  setNominatedPersonName = (value: string) =>
    runInAction(() => {
      this.nominatedPersonName = value;
      this.nominatedPersonDirty = true;
    });

  /**
   * Get nominated person as firstName, lastName, mobile
   */
  get nominatedPersonParts() {
    const parts = this.nominatedPersonName.split('\t');
    return {
      firstName: parts?.[0],
      lastName: parts?.[1],
      mobile: parts?.[2],
    };
  }

  /**
   * This can be used instead of setNominatedPersonName if you want to set first last and mobile the parts will be tab delimited
   * see getter nominatedPersonParts
   * @param param0
   */
  setNominatedPersonParts({
    firstName,
    lastName,
    mobile,
  }: {
    firstName: string;
    lastName: string;
    mobile: string;
  }) {
    this.setNominatedPersonName(`${firstName}\t${lastName}\t${mobile}`);
  }

  updateGiftCardRecipientDetails = async (
    giftCardRecipientData: GiftCardRecipientData,
  ) => apiService.updateGiftCardRecipientInformation(giftCardRecipientData);

  /**
   * Set chill drinks from UI
   * @param value
   */
  setChillDrinks(value: boolean) {
    runInAction(() => (this.chillDrinks = value));
    this.updateOrderNotes();
  }

  get orderNotesWithChillDrinks() {
    return (this.chillDrinks ? CHILL_DRINKS : '') + this.orderNotes;
  }

  updateOrderNotes = () => {
    if (this.orderNotesWithChillDrinks !== this.currentOrder?.note) {
      const order = {
        note: this.orderNotesWithChillDrinks,
      };
      apiService
        .updateOrderNote(order)
        .then((res: any) => runInAction(() => (this.currentOrder = res.data)))
        .catch(err => console.error('unable to set order note store', err));
    }
  };

  // We can save an address or instructions only for a delivery and only if a timeslot is selected.
  // Pickup doesn't have any address or instructions, so we can get away with just the timeslot.
  // I don't really want to double call the API, but we have to force validation as
  // the order lines (especially the pickup/delivery fees) aren't recalculated.
  updateDeliveryDetails = () => {
    const order: any = {};
    if (this.selectedSlot) {
      order.time_slot_id = this.selectedSlot.id;
    }
    if (this.isDelivery) {
      if (this.deliveryAddress && this.selectedSlot) {
        order.delivery_address = this.deliveryAddress;
      }
      // Delivery instructions won't be accepted without a delivery slot having been chosen.
      if (this.deliveryInstructions && this.selectedSlot) {
        order.instructions = this.deliveryInstructions;
        order.instructions += this.deliveryInstructions.length ? '\n' : '';
      }
      // Add nominated person to delivery instructions.
      if (this.willBePresent && this.selectedSlot) {
        order.instructions = order.instructions || '';
        order.instructions += `**${SELF_DELIVERY}${this.rootStore?.userStore.firstName} ${this.rootStore?.userStore.lastName}**`;
      } else if (this.nominatedPersonName && this.selectedSlot) {
        order.instructions = order.instructions || '';
        order.instructions += `**${NOMINATED_DELIVERY}${this.nominatedPersonName}**`;
        order.delivery_recipient_name = this.nominatedPersonName;
      }
    }
    // Don't update if it's empty
    if (Object.keys(order).length) {
      apiService
        .updateDeliveryDetails(order)
        .then(() => {
          // Ignore whatever MFL give back to us and validate to force recalculation of the order lines.
          this.validateOrder();
        })
        .catch(err => console.error('unable to set delivery details', err))
        .finally(() => {
          runInAction(() => {
            this.selectedSlotDirty = false;
            this.deliveryAddressDirty = false;
            this.nominatedPersonDirty = false;
            this.deliveryInstructionsDirty = false;
          });
          if (this._afterUpdate) {
            this._afterUpdate?.();
            this._afterUpdate = null;
          }
        });
    } else {
      if (this._afterUpdate) {
        this._afterUpdate?.();
        this._afterUpdate = null;
      }
    }
  };

  fullyReloadOrderFromMFL = () => {
    this.reset();
    return this.loadOrderForUser();
  };

  loadOrderForUser = async () => {
    try {
      runInAction(() => {
        this.isLoadingOrder = true;
        this.forceLoadRegion = true;
      });
      const data = await apiService.loadCurrentOrder();
      runInAction(() => {
        this.currentOrder = data;
        this.dirtyOrder = false;
        this.zenGiftCardRecipientFormValues =
          getZenGiftCardRecipientFormValuesFromOrder(data);
      });
    } catch (error) {
      console.log('Unable to load order for user', error);
    }
    runInAction(() => (this.isLoadingOrder = false));
  };

  loadRecentOrders = () => {
    runInAction(() => {
      this.loadingRecentOrders = true;
      this.recentOrders = [];
    });
    apiService
      .loadRecentOrders()
      .then(data => {
        runInAction(() => {
          this.loadingRecentOrders = false;
          this.recentOrders = data
            .filter((o: any) => !!o.order_number)
            .sort((a: any, b: any) => b.order_number - a.order_number);
        });
      })
      .catch(err => {
        runInAction(() => {
          this.loadingRecentOrders = false;
          this.recentOrders = [];
        });
        console.log('unable to load recent orders for user', err);
      });
  };

  setItemQty = (productId: any, quantity: number) => {
    const orderUpdate = [...this.orderUpdate];
    const currentOrderItem = orderUpdate.find(
      item => item.productId === productId,
    );
    if (currentOrderItem) {
      currentOrderItem.quantity = quantity;
    } else {
      orderUpdate.push({productId: productId, quantity: quantity});
    }
    runInAction(() => {
      this.orderUpdate = orderUpdate;
      this.dirtyOrder = true;
    });
  };

  getItemQty = (itemId: any) => {
    const found = this.currentOrder?.order_lines?.find(
      (line: any) => line.product_id === itemId,
    );
    return found?.quantity || 0;
  };

  getTotalOrderCount = () => {
    // Sum up the lines on the order, ignore charges and anything that is
    // in the orderUpdate object.
    const orderTotal =
      this.currentOrder?.order_lines
        ?.filter(
          (ol: any) =>
            !ol.charge &&
            !this.orderUpdate.find(col => col.productId === ol.product_id),
        )
        .map((ol: any) => ol.quantity)
        .reduce((a: any, b: any) => a + b, 0) || 0;

    const updateTotals =
      this.orderUpdate?.map(ou => ou.quantity).reduce((a, b) => a + b, 0) || 0;

    return orderTotal + updateTotals;
  };

  updateOrder = async () => {
    const update = this.orderUpdate.map(line => ({
      product_id: line.productId,
      quantity: line.quantity,
    }));

    runInAction(() => {
      this.updatingOrder = true;
    });

    try {
      const data = await apiService.changeOrderLines(update);

      const giftCardsWithReadjustedChargeAmounts =
        readjustGiftCardsChargeAmounts(
          data,
          this.zenGiftCardsUserPaymentInstruments,
          this.switchingDeliveryPickup,
        );

      runInAction(() => {
        this.currentOrder = data;
        this.orderUpdate = [];
        this.dirtyOrder = false;
        this.updatingOrder = false;
        this.zenGiftCardsUserPaymentInstruments =
          giftCardsWithReadjustedChargeAmounts;
      });
    } catch (err) {
      console.log('unable to update order line', err);
      runInAction(() => {
        this.dirtyOrder = false;
        this.orderUpdate = [];
        this.currentOrder = null;
        this.updatingOrder = false;
      });
      this
        .loadOrderForUser
        // TODO: this method doesn't actually receive any arguments...is the
        // store id really needed for something?
        // this.rootStore.shopSelectionStore.selectedStore.id,
        ();
    }
  };

  updateOrderDirectly = async (
    orderUpdate: {
      product_id: string;
      quantity: number;
    }[],
  ) => {
    try {
      const data = await apiService.changeOrderLines(orderUpdate);

      runInAction(() => {
        this.currentOrder = data;
        this.orderUpdate = [];
        this.dirtyOrder = false;
        this.updatingOrder = false;
      });
    } catch (err) {
      console.log('unable to update order line', err);
      runInAction(() => {
        this.dirtyOrder = false;
        this.orderUpdate = [];
        this.currentOrder = null;
        this.updatingOrder = false;
      });
      this
        .loadOrderForUser
        // TODO: this method doesn't actually receive any arguments...is the
        // store id really needed for something?
        // this.rootStore.shopSelectionStore.selectedStore.id,
        ();
    }
  };

  updateCoupon = (coupon: null) => {
    runInAction(() => {
      this.dirtyOrder = true;
      this.updatingOrder = true;
    });
    apiService
      .updateCoupon(coupon)
      .then(data => {
        runInAction(() => {
          this.currentOrder = data.order ? data.order : data;
          this.updatingOrder = false;
          this.dirtyOrder = false;
        });
        Toast.show({type: SUCCESS_CHECK});
      })
      .catch(err => {
        console.log('update coupon error:', err);
        let errorMessage = "We couldn't apply your coupon. Please try again.";
        if (Array.isArray(err.response?.data?.errors)) {
          errorMessage = err.response.data.errors
            .map((e: {detail: any}) => e.detail)
            .join('\n');
        } else if (
          isString(err.response?.data) &&
          isUnhandledMFL500Error(err.response.data)
        ) {
          // TMP: Coupons are important. We need to be able to quickly differentiate when MFL returns just HTTP 500
          // 2592-Coupons: Unhandled MFL error when adding coupon (https://dev.azure.com/lmgdevops/branded_website/_workitems/edit/2592)
          // In the meantime: ECOMM -> Ecommerce -> MFL, 500 -> HTTP 500
          errorMessage = `${errorMessage} (ECOMM-500)`;
        }
        runInAction(() => {
          this.updatingOrder = false;
          this.dirtyOrder = false;
        });
        Toast.show({type: ERROR_MESSAGE, text1: errorMessage});
      });
  };

  validateOrder = () => {
    runInAction(() => (this.submittingOrder = true));
    apiService
      .validateOrder()
      .then(data => {
        runInAction(() => {
          this.currentOrder = data;
          this.submittingOrder = false;
        });
      })
      .catch(err => {
        console.log('unable to validate order', err.message);
        runInAction(() => (this.submittingOrder = false));
      });
  };

  pollOrderStatus = (navigation: OrderProcessingScreenProps['navigation']) => {
    apiService
      .pollOrder(this.currentOrder.id)
      .then(async (order: OrderResponse) => {
        const tenders = order.tenders || [];
        const isAllTendersCompletelySucceeded = tenders.every(
          tender => tender.succeeded && !tender.failed && !tender.in_doubt,
        );
        const isAtLeastOneTenderFailed = tenders.some(tender => tender.failed);
        const isNoneTenderFailed = !isAtLeastOneTenderFailed;
        const isAtLeastOneTenderInDoubtWithNoFailedTenders =
          isNoneTenderFailed && tenders.some(tender => tender.in_doubt);

        if (
          order.customer_interaction_data &&
          order.customer_interaction_data.redirect_url
        ) {
          // Order payment got challenged by 3DS
          runInAction(() => {
            this.currentOrder = order;
            this.orderPayment3DSChallenged = true;
          });
          navigation.navigate(AppRoute.THREEDS_IFRAME_FULLSCREEN, {
            url: order.customer_interaction_data.redirect_url,
            navigationTarget: AppRoute.CART_ORDER_PROCESSING,
          });
        } else if (
          order.checkout?.started_at &&
          order.checkout?.succeeded &&
          !order.checkout?.failed &&
          !order.checkout?.in_doubt &&
          order.order_number &&
          order.balance?.cents_inc_tax === 0 &&
          isAllTendersCompletelySucceeded
        ) {
          Analytics.logEvent({
            eventName: EventName.Purchase,
            parameters: {
              screen_name: EventScreenName.Checkout,
              transaction_id: order.id,
              order_number: order?.order_number,
              value: order.total?.cents_inc_tax / 100,
              currency: 'AUD',
              tax: order.total?.cents_tax / 100,
              ...(order.total?.iso_currency_code && {
                currency_code: order.total?.iso_currency_code,
              }),
              ...(order?.coupon_code && {coupon: order.coupon_code}),
              items: order.order_lines.map(item => ({
                item_id: item.product_id,
                item_name: item.name,
                item_brand: item.brand_name ?? '',
                price: item.sell_price_cents / 100,
                quantity: item.quantity,
                discount:
                  (item.normal_price_cents - item.sell_price_cents) / 100,
              })),
            },
          });

          // Order was successful in checking out
          // Check if the order is eligible for competition entry
          let comps: any = [];
          try {
            comps = await apiService.getCompetitionsForOrder(order.id);
          } catch (ex) {
            console.log(
              'failed checking competitions for order:',
              order.id,
              order.order_number,
              order.checkout,
            );
          }

          // Get a set of distinct activations that this order is eligible for.
          const eligibleCompetitionsSet = new Set<AirtablePromotionItem>();
          comps.forEach((comp: any) => {
            let matched =
              this.rootStore?.promotionsStore.activePromotions.filter(
                (act: {activationUrlName: string}) => {
                  const eligibleUrl = comp.name
                    .replaceAll(' ', '-') // MFL identifier for competition uses spaces instead of hyphens
                    .toLowerCase();
                  return act.activationUrlName?.toLowerCase() === eligibleUrl;
                },
              ) || [];

            matched.forEach((act: any) =>
              eligibleCompetitionsSet.add({
                ...act,
                mflId: comp.id,
              }),
            );
          });
          let eligibleCompetitionsList = Array.from(eligibleCompetitionsSet);

          // Temporary: Sumer Cash Grab ----------------------------------
          // Place the Sumer Cash Grab competition at the bottom of the list
          const summerCashGrabCompetition = eligibleCompetitionsList.find(
            isSummerCashGrabCompetition,
          );
          if (summerCashGrabCompetition) {
            eligibleCompetitionsList = eligibleCompetitionsList.filter(
              comp => !isSummerCashGrabCompetition(comp),
            );
            // Add the Summer Cash Grab competition to the bottom of the list since the user can't go 'back' to promotions list
            eligibleCompetitionsList.push(summerCashGrabCompetition);
          }
          console.log('👾', 'eligibleCompetitionsList', eligibleCompetitionsList);
          console.log('👾', 'eligibleCompetitionsSet.size', eligibleCompetitionsSet.size);
          //----------------------------------------------------------------

          runInAction(() => {
            this.eligibleCompetitions = eligibleCompetitionsList;
            this.orderProcessing = false;
            this.currentOrder = order;
            this.orderCheckedOutSuccess = true;
          });
          if (eligibleCompetitionsSet.size) {
            console.log('👾', 'Navigating to CART_ORDER_COMPETITIONS');
            navigation.navigate(AppRoute.CART_ORDER_COMPETITIONS);
          } else {
            if (
              this.rootStore?.shopSelectionStore.isSelectedStoreGiftCardStore()
            ) {
              navigation.navigate(AppRoute.GIFT_CARDS_MAIN, {
                screen: AppRoute.GIFT_CARDS_STORE_ORDER_CONFIRMATION,
              });
            } else {
              navigation.navigate(AppRoute.CART_ORDER_CONFIRMATION);
            }
          }
        } else if (
          order.checkout?.started_at &&
          order.checkout?.failed &&
          isAtLeastOneTenderFailed
        ) {
          // Order failed a payment
          runInAction(() => {
            this.currentOrder = order;
            this.orderFailedPayment = true;
          });
        } else if (
          order.checkout?.started_at &&
          order.checkout?.in_doubt &&
          isAtLeastOneTenderInDoubtWithNoFailedTenders
        ) {
          runInAction(() => {
            this.currentOrder = order;
            this.orderPaymentIncomplete = true;
          });
        }
      })
      .catch(err => {
        console.log('err', err);
      });
  };

  checkoutOrder = async (
    navigation: any,
    browserData: {
      java_enabled: boolean;
      language: string;
      color_depth: number;
      screen_height: number;
      screen_width: number;
      timezone: number;
    },
  ) => {
    runInAction(() => {
      this.orderProcessing = true;
      this.submittingOrder = true;
    });
    try {
      // This will fail if for example there's no credit card registered in the
      // account and the tender options are not yet set:
      // await apiService.validateOrder();

      if (this.isGiftCardsBeingUsed()) {
        await this.updateCurrentOrderGiftCardTenderOptions();
      }

      await apiService.validateOrder();

      const checkoutRequest = {
        payment_instrument_id:
          this.rootStore?.paymentStore.tillUserPaymentInstrument?.id,
        browser_information: {
          ...browserData,
        },
      };

      // Tender Options - Returns 422 with tender_options capability and a payment instrument is sent
      // https://myfoodlink.github.io/mfl-smart-shopping-api-docs/shop__orders__validate/tender_options_-_returns_422_with_tender_options_capability_and_a_payment_instrument_is_sent.html
      if (this.isGiftCardsBeingUsed()) {
        checkoutRequest.capabilities = ['tender_options'];
        checkoutRequest.payment_instrument_id = undefined;
      }

      await apiService.checkoutOrder(checkoutRequest);

      runInAction(() => (this.submittingOrder = false));
      navigation.navigate(AppRoute.CART_ORDER_PROCESSING);
    } catch (err: any) {
      const out = [];
      if (err.response?.status === 422) {
        if (typeof err?.response?.data === 'string') {
          out.push(err?.response?.data);
        } else {
          err.response?.data?.errors?.forEach((error: {detail: any}) =>
            out.push(error.detail),
          );
        }
      }
      runInAction(() => (this.submittingOrder = false));
      Toast.show({
        type: ERROR_MESSAGE,
        text1: out.length
          ? out.join('\n')
          : 'Unable to process order at this time.',
      });
    } finally {
      this.rootStore?.resetOffersStore();
    }
  };

  getProductsPromotedAtCheckout = () =>
    apiService
      .searchPromotedAtCheckout(
        this.rootStore?.shopSelectionStore.selectedStore
          ?.promote_at_checkout_search?.id,
      )
      .then(res => {
        runInAction(() => (this.promotedAtCheckoutProducts = res));
      })
      .catch(() => {
        console.log('unable to search promoted at checkout products');
        return null;
      });

  setOrderPayment3DSChallenged = (orderPayment3DSChallenged: boolean) => {
    this.orderPayment3DSChallenged = orderPayment3DSChallenged;
  };

  // Gift Cards
  setGiftCardRecipientFormValues = (
    giftCardRecipientFormValues: GiftCardData,
  ) => (this.zenGiftCardRecipientFormValues = giftCardRecipientFormValues);

  addZenGiftCardsUserPaymentInstrument = async (
    cardNumber: string,
    pin: string,
  ) => {
    const existingGiftCard = this.zenGiftCardsUserPaymentInstruments.find(
      card => card.zen_card_number === cardNumber,
    );
    if (existingGiftCard) {
      throw new Error('Gift card already added');
    }

    const request: CreateZenGiftCardPaymentInstrumentRequest = {
      order_id: this.currentOrder?.id,
      service_id:
        this.rootStore?.paymentStore.zenGiftCardsPaymentService?.id || '',
      zen_gift_card: {
        gift_card_number: cardNumber,
        gift_card_pin: pin,
      },
    };
    const response = (await apiService.createZenGiftCardPaymentInstrument(
      request,
    )) as unknown as CreateZenGiftCardPaymentInstrumentResponse;

    const paymentInstrument = response?.payment_instrument;
    if (!paymentInstrument) {
      throw new Error('Payment instrument not found');
    }

    const balance = paymentInstrument.balance || 0;
    if (balance === 0) {
      throw new Error('The gift card has no remaining balance');
    }

    // everything in cents
    const paymentInstrumentWithBalance: ZenGiftCardsPaymentInstrument = {
      ...paymentInstrument,
      balance,
    };

    const totalChargedOnGiftCards = this.getTotalChargedOnGiftCardsCents();
    const giftCardBalance = paymentInstrumentWithBalance.balance || 0;
    const orderTotalAmountRemaining =
      this.totalAmount - totalChargedOnGiftCards;
    const chargedAmount = Math.min(giftCardBalance, orderTotalAmountRemaining);
    const paymentInstrumentWithChargedAmount: ZenGiftCardsPaymentInstrumentWithChargedAmount =
      {
        ...paymentInstrumentWithBalance,
        chargedAmount,
      };

    runInAction(() =>
      this.zenGiftCardsUserPaymentInstruments.push(
        paymentInstrumentWithChargedAmount,
      ),
    );
  };

  deleteZenGiftCardsUserPaymentInstrument = (id: string) => {
    const remainingZenGiftCardsUserPaymentInstruments: ZenGiftCardsPaymentInstrumentWithChargedAmount[] =
      this.zenGiftCardsUserPaymentInstruments.filter(card => card.id !== id);

    const giftCardsWithReadjustedChargeAmounts = readjustGiftCardsChargeAmounts(
      this.currentOrder,
      remainingZenGiftCardsUserPaymentInstruments,
      this.switchingDeliveryPickup,
    );

    runInAction(() => {
      this.zenGiftCardsUserPaymentInstruments =
        giftCardsWithReadjustedChargeAmounts;
    });
  };

  clearCurrentOrderTenderOptions = async () =>
    apiService.updateCurrentOrderTenderOptions({order: {tender_options: []}});

  updateCurrentOrderGiftCardTenderOptions = async () => {
    const isGiftCardStore =
      this.rootStore?.shopSelectionStore.isSelectedStoreGiftCardStore();

    if (isGiftCardStore) {
      return;
    }

    const giftCardsWithChargedAmount =
      this.zenGiftCardsUserPaymentInstruments.filter(
        card => card.chargedAmount > 0,
      );
    const tenderOptions = giftCardsWithChargedAmount.map(card => ({
      payment_instrument_id: card.id,
      amount_to_charge: {
        type: 'up_to',
        amount: card.chargedAmount, // cents
      },
    }));

    const request = {
      order: {
        tender_options: tenderOptions,
      },
    };

    if (tenderOptions.length) {
      const totalChargedOnGiftCards = this.getTotalChargedOnGiftCardsCents();
      const orderTotalAmountRemaining =
        this.totalAmount - totalChargedOnGiftCards; // cents

      if (orderTotalAmountRemaining > 0) {
        const tillUserPaymentInstrumentId =
          this.rootStore?.paymentStore.tillUserPaymentInstrument?.id;
        if (tillUserPaymentInstrumentId) {
          request.order.tender_options.push({
            payment_instrument_id: tillUserPaymentInstrumentId,
            amount_to_charge: {type: 'remainder'},
          });
        }
      }
    }

    await apiService.updateCurrentOrderTenderOptions(request);
  };

  // Taken from OrderTotals.tsx. That need to be refactored but I'm afraid to
  // touch that at this moment. Critical area that has not been changed
  // since the original version.
  getCartSubtotal = (): number =>
    this.currentOrder?.order_lines
      ?.filter((ol: any) => !ol.charge)
      .map((ol: any) => ol.subtotal?.cents_inc_tax)
      .reduce(sum, 0) || 0;

  getCartGstIncluded = () => this.currentOrder?.total?.cents_tax || 0;

  getCartAdjustments = () =>
    this.currentOrder?.adjustments
      ? this.currentOrder.adjustments
          .filter(
            (adj: any) =>
              !!adj.name && !adj.name.toLowerCase().includes('delivery'),
          )
          .map((adj: any) => ({
            name: adj.name,
            amount: adj.amount.cents_inc_tax,
          }))
      : [];

  getCartDiscountsAndCredits = () =>
    this.currentOrder?.discounts_and_credits?.map((dac: any) => ({
      name: dac.narrative,
      amount: -1 * dac.amount,
    })) || [];

  getTotalChargedOnGiftCardsCents = () =>
    this.zenGiftCardsUserPaymentInstruments.reduce(
      (acc, card) => acc + card.chargedAmount,
      0,
    );

  isGiftCardsBeingUsed = () =>
    this.zenGiftCardsUserPaymentInstruments.length &&
    this.getTotalChargedOnGiftCardsCents() > 0;
}
